import React from 'react';
import Row from 'react-bootstrap/Row';
import classnames from 'classnames';
import isURL from 'validator/lib/isURL';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

export interface IVehicleIntegrationConfigModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<any>) => void;
  onSubmit: (integrationCondfig: IConfigEntityIntegrations) => void;
  integrationConfig: IConfigEntityIntegrations;
}

interface IVehicleIntegrationConfigModalState {
  integrationConfig: IVehicleEntityIntegrationConfig;
  error: {
    apiKey: IFieldValidation;
    plateDecoderURL: IFieldValidation;
    plateRecognitionURL: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMsgGen = new ErrorMessageGenerator();

export class VehicleIntegrationConfigModal extends React.Component<
  IVehicleIntegrationConfigModalProps,
  IVehicleIntegrationConfigModalState
> {
  constructor(props: IVehicleIntegrationConfigModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  getEmptyState = (): IVehicleIntegrationConfigModalState => ({
    integrationConfig: {
      apiKey: '',
      plateDecoderURL: '',
      plateRecognitionURL: '',
    },
    error: {
      apiKey: {
        errorMessage: '',
        errorState: null,
      },
      plateDecoderURL: {
        errorMessage: '',
        errorState: null,
      },
      plateRecognitionURL: {
        errorMessage: '',
        errorState: null,
      },
    },
  });

  componentDidUpdate(prevProps: IVehicleIntegrationConfigModalProps) {
    const { integrationConfig } = this.props;
    const hasOpened = this.props.show !== prevProps.show && this.props.show;
    const isEdit = !!this.props.integrationConfig;

    if (hasOpened && isEdit) {
      this.setState({
        integrationConfig: {
          ...integrationConfig.licensePlateService,
        },
        error: this.getEmptyState().error,
      });
    }

    if (hasOpened && !isEdit) {
      this.setState(this.getEmptyState());
    }
  }

  onSubmit = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (this.isValid()) {
      this.props.onSubmit(this.getRequestFromState());
    }
  };

  getRequestFromState = (): IConfigEntityIntegrations => {
    const { apiKey, plateDecoderURL, plateRecognitionURL } = this.state.integrationConfig;

    return {
      licensePlateService: {
        apiKey,
        plateDecoderURL,
        plateRecognitionURL,
      },
    };
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      integrationConfig: {
        ...prevState.integrationConfig,
        [name]: value,
      },
    }));
  };

  isValid = () => {
    const { integrationConfig } = this.state;
    const error = this.getEmptyState().error;

    let isValid = true;

    if (cyberdyneValidator.isEmptyString(integrationConfig.apiKey)) {
      error.apiKey = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getMandatoryFieldMessage('API Key'),
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(integrationConfig.apiKey, 500)) {
      error.apiKey = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getCharacterLimitMessage('API Key', 500),
      };
      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(integrationConfig.plateDecoderURL)) {
      error.plateDecoderURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getMandatoryFieldMessage('Plate decoder URL'),
      };
      isValid = false;
    } else if (!isURL(integrationConfig.plateDecoderURL)) {
      error.plateDecoderURL = {
        errorState: IValidationState.ERROR,
        errorMessage: 'Enter valid Plate decoder URL.',
      };
      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(integrationConfig.plateDecoderURL, 500)) {
      error.plateDecoderURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getCharacterLimitMessage('Plate decoder URL', 500),
      };

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(integrationConfig.plateRecognitionURL)) {
      error.plateRecognitionURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getMandatoryFieldMessage('Plate recognition URL'),
      };
      isValid = false;
    } else if (!isURL(integrationConfig.plateRecognitionURL)) {
      error.plateRecognitionURL = {
        errorState: IValidationState.ERROR,
        errorMessage: 'Enter valid Plate recognition URL',
      };
      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(integrationConfig.plateRecognitionURL, 500)) {
      error.plateRecognitionURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getCharacterLimitMessage('Plate recognition URL', 500),
      };

      isValid = false;
    }

    this.setState({
      error,
    });

    return isValid;
  };

  renderTitle = () =>
    this.props.integrationConfig ? 'Edit License Plate Recognition' : 'Configure License Plate Recognition';

  renderBody = () => {
    const { integrationConfig, error } = this.state;

    return (
      <div className={classnames('integration__body')}>
        <Row>
          <Input
            field={{
              label: 'API Key',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: integrationConfig.apiKey || '',
            }}
            error={error.apiKey}
            width={4}
            name="apiKey"
            onChange={this.handleChange}
          />
          <Input
            field={{
              label: 'Plate Decoder URL',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: integrationConfig.plateDecoderURL || '',
            }}
            error={error.plateDecoderURL}
            width={4}
            name="plateDecoderURL"
            onChange={this.handleChange}
          />
        </Row>
        <Row>
          <Input
            field={{
              label: 'Plate Recognition URL',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: integrationConfig.plateRecognitionURL || '',
            }}
            error={error.plateRecognitionURL}
            width={4}
            name="plateRecognitionURL"
            onChange={this.handleChange}
          />
        </Row>
      </div>
    );
  };

  renderFooter = () => {
    const submitBtnTxt = this.props.integrationConfig ? 'Save' : 'Add Integration';

    return (
      <div className={classnames('integration__footer')}>
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.props.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitBtnTxt} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('integration-modal')}
      />
    );
  }
}

export default VehicleIntegrationConfigModal;
